import ApiHttp from "./ApiHttp";
import Address from "./Address";
import { MODEL_STR } from "./Role";

type AffiliatedOrganization = {
  uuid: API.OrganizationId;
  name_legal: string;
  name_dba: string | null;
  core_id: string;
  address: API.Address | null;
};

export default class Organization {
  public uuid: API.OrganizationId;

  public name_legal: string;

  public name_dba: string | null;

  public address: Address;

  public affiliated_organizations?: AffiliatedOrganization[];

  public ach_dual_approval_required: boolean;

  public dual_approval_required: boolean;

  public dual_approval_user_control: boolean;

  public dual_approval_admin_exemption: boolean;

  public requires_some_dual_approvals: boolean;

  public international_wire_dual_approval_required: boolean;

  public ach_template_dual_approval_required: boolean;

  public ach_dual_approval_expiration_grace_period_days?: number;

  constructor(props: any) {
    this.uuid = props.uuid;
    this.name_legal = props.name_legal;
    this.name_dba = props.name_dba;
    this.address = props.address;
    this.affiliated_organizations = props.affiliated_organizations;

    this.ach_dual_approval_required = props.ach_dual_approval_required;
    this.dual_approval_required = props.dual_approval_required;
    this.dual_approval_user_control = props.dual_approval_user_control;
    this.dual_approval_admin_exemption = props.dual_approval_admin_exemption;
    this.requires_some_dual_approvals = props.requires_some_dual_approvals;
    this.international_wire_dual_approval_required =
      props.international_wire_dual_approval_required;
    this.ach_template_dual_approval_required =
      props.ach_template_dual_approval_required;
    this.ach_dual_approval_expiration_grace_period_days =
      props.ach_dual_approval_expiration_grace_period_days;
  }

  static async getOrganization(
    orgUuid: UUID | API.OrganizationId,
  ): Promise<Organization> {
    const response = await ApiHttp.fetch(`organizations/${orgUuid}/`, {
      method: "GET",
    });
    return Organization.deserialize(response);
  }

  static async setOrganization(
    orgUuid: UUID | API.OrganizationId,
    method: "GET" | "POST" | "PATCH" = "PATCH",
    value: any,
  ): Promise<Organization> {
    return ApiHttp.fetch(
      `organizations/${orgUuid}/`,
      {
        method,
      },
      value,
    ).then((response) => Organization.deserialize(response));
  }

  static getNumberOfDualApprovalRequests() {
    return ApiHttp.fetch(`approval_requests`, {
      method: "GET",
      params: { exclude_user_requests: "true" },
    }).then((res) => res.meta.total);
  }

  static getOrganizationHasMultipleApprovalUsers(
    organizationUsers: API.OrganizationUser[],
  ): boolean {
    if (!organizationUsers) {
      return false;
    }
    const hasApprovalPermission = (organizationUser: API.OrganizationUser) =>
      organizationUser.role.permissions.some(
        (p) =>
          ["*", MODEL_STR.DUAL_APPROVAL_REQUEST].includes(p.model_str) &&
          ["*", "write"].includes(p.operation),
      );
    return organizationUsers.filter(hasApprovalPermission).length > 1;
  }

  updateBusinessAddress(
    address: Address,
    deviceId: string,
    deviceRequestId: string,
  ): Promise<Address> {
    return ApiHttp.fetch(
      `organizations/${this.uuid}/address`,
      { method: "PUT" },
      { device_id: deviceId, device_request_id: deviceRequestId, ...address },
    ).then((response) => new Address(response));
  }

  /*
   *  Factory method returning a new instance of Organization from
   *  an ApiHttp serialized Organization
   */
  static deserialize(payload: any) {
    return new Organization({
      uuid: payload.uuid,
      name_legal: payload.name_legal,
      name_dba: payload.name_dba,
      ach_dual_approval_required: payload.ach_dual_approval_required,
      dual_approval_required: payload.dual_approval_required,
      dual_approval_user_control: payload.dual_approval_user_control,
      dual_approval_admin_exemption: payload.dual_approval_admin_exemption,
      requires_some_dual_approvals: payload.requires_some_dual_approvals,
      international_wire_dual_approval_required:
        payload.international_wire_dual_approval_required,
      ach_template_dual_approval_required:
        payload.ach_template_dual_approval_required,
      address: new Address(payload.address),
      ach_dual_approval_expiration_grace_period_days:
        payload.ach_dual_approval_expiration_grace_period_days,
      affiliated_organizations: payload.affiliated_organizations,
    });
  }

  serialize() {
    return {
      uuid: this.uuid,
      ach_dual_approval_required: this.ach_dual_approval_required,
      dual_approval_required: this.dual_approval_required,
      dual_approval_user_control: this.dual_approval_user_control,
      dual_approval_admin_exemption: this.dual_approval_admin_exemption,
      requires_some_dual_approvals: this.requires_some_dual_approvals,
      international_wire_dual_approval_required:
        this.international_wire_dual_approval_required,
      ach_template_dual_approval_required:
        this.ach_template_dual_approval_required,
      address: this.address.toJSON(),
      ach_dual_approval_expiration_grace_period_days:
        this.ach_dual_approval_expiration_grace_period_days,
      affiliated_organizations: this.affiliated_organizations,
    };
  }
}
